import type { Locale, RouteKey } from "@/types";
import type { CaseStudyMeta } from "@/content/caseStudies";
import {
  getPlatformTool,
  type PlatformToolId,
} from "@/content/platformTools";
import {
  getUseCaseSector,
  type UseCaseSectorId,
} from "@/content/useCaseSectors";
import { getBlogPost, type BlogPostId } from "@/content/blog/posts";

type SlugMap = Record<RouteKey, Record<Locale, string>>;

/**
 * Path segment for each route, per locale. FR is the default (no prefix);
 * EN is served under /en/. Slugs are intentionally different per locale.
 */
export const ROUTE_SLUGS: SlugMap = {
  home: { fr: "", en: "" },
  day: { fr: "ta-journee", en: "your-day" },
  platform: { fr: "fonctionnalites", en: "features" },
  businessSoftware: { fr: "logiciel-ia-pme-canadiennes", en: "canadian-ai-business-software" },
  useCases: { fr: "cas-d-usage", en: "use-cases" },
  caseStudies: { fr: "etudes-de-cas", en: "case-studies" },
  pricing: { fr: "abonnements", en: "plans" },
  security: { fr: "securite", en: "security" },
  integrations: { fr: "integrations", en: "integrations" },
  blog: { fr: "blogue", en: "blog" },
  about: { fr: "a-propos", en: "about" },
  contact: { fr: "contact", en: "contact" },
  legalPrivacy: { fr: "confidentialite", en: "privacy" },
  legalTerms: { fr: "conditions", en: "terms" },
  legalCookies: { fr: "temoins", en: "cookies" },
  legalAccessibility: { fr: "accessibilite", en: "accessibility" },
  demo: { fr: "demo", en: "demo" },
  login: { fr: "connexion", en: "login" },
  signup: { fr: "inscription", en: "signup" },
  ric: { fr: "ric", en: "ric" },
  notFound: { fr: "404", en: "404" },
};

export function resolveRoute(key: RouteKey, locale: Locale): string {
  const slug = ROUTE_SLUGS[key][locale];
  const prefix = locale === "en" ? "/en" : "";
  if (!slug) return prefix || "/";
  return `${prefix}/${slug}`;
}

export const SITE_ORIGIN = "https://www.reachcentral.io";

export function resolveCaseStudyRoute(study: CaseStudyMeta, locale: Locale): string {
  return `${resolveRoute("caseStudies", locale)}/${study.slugs[locale]}`;
}

export function absoluteUrl(key: RouteKey, locale: Locale): string {
  return `${SITE_ORIGIN}${resolveRoute(key, locale)}`;
}

function prefixFor(locale: Locale): string {
  return locale === "en" ? "/en" : "";
}

/**
 * Resolve a /fonctionnalites/[tool] or /en/features/[tool] path from a tool id.
 * Throws if the id is not in the platform-tool registry — callers should
 * always derive ids from PLATFORM_TOOLS so this fault-trips at dev time.
 */
export function resolvePlatformToolRoute(
  toolId: PlatformToolId,
  locale: Locale,
): string {
  const tool = getPlatformTool(toolId);
  if (!tool) throw new Error(`Unknown platform tool id: ${toolId}`);
  return `${prefixFor(locale)}/${ROUTE_SLUGS.platform[locale]}/${tool.slugs[locale]}`;
}

export function absolutePlatformToolUrl(
  toolId: PlatformToolId,
  locale: Locale,
): string {
  return `${SITE_ORIGIN}${resolvePlatformToolRoute(toolId, locale)}`;
}

/**
 * Resolve a /cas-d-usage/[sector] or /en/use-cases/[sector] path from a sector id.
 */
export function resolveUseCaseRoute(
  sectorId: UseCaseSectorId,
  locale: Locale,
): string {
  const sector = getUseCaseSector(sectorId);
  if (!sector) throw new Error(`Unknown use-case sector id: ${sectorId}`);
  return `${prefixFor(locale)}/${ROUTE_SLUGS.useCases[locale]}/${sector.slugs[locale]}`;
}

export function absoluteUseCaseUrl(
  sectorId: UseCaseSectorId,
  locale: Locale,
): string {
  return `${SITE_ORIGIN}${resolveUseCaseRoute(sectorId, locale)}`;
}

/**
 * Resolve a /blogue/[slug] or /en/blog/[slug] path from a post id.
 */
export function resolveBlogPostRoute(
  postId: BlogPostId,
  locale: Locale,
): string {
  const post = getBlogPost(postId);
  if (!post) throw new Error(`Unknown blog post id: ${postId}`);
  return `${prefixFor(locale)}/${ROUTE_SLUGS.blog[locale]}/${post[locale].frontmatter.slug}`;
}

export function absoluteBlogPostUrl(
  postId: BlogPostId,
  locale: Locale,
): string {
  return `${SITE_ORIGIN}${resolveBlogPostRoute(postId, locale)}`;
}
